import { applyPageMetadata } from "@/lib/page-metadata";
import { LastUpdated } from "@/components/site/LastUpdated";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout, Section, Eyebrow, CTAButton, BOOKING_URL } from "@/components/site/Layout";
import { CaseStudy, serviceCaseStudy } from "@/components/site/CaseStudy";
import { TrustSignals } from "@/components/site/TrustSignals";
import { FAQSection } from "@/components/site/FAQSection";
import { HeroLeadForm } from "@/components/site/HeroLeadForm";
import ContentPlanBuilder from "@/components/blog/ContentPlanBuilder";
import { Check } from "lucide-react";
import { services } from "@/data/services";
import { jsonLdScript, ogImageFor } from "@/lib/seo";

const TITLE = "Legal Content Writing for Law Firms | PageOne Lawyers";
const DESCRIPTION =
  "Legal content writing for law firms by experienced legal writers. Practice pages, blog posts and website copy your attorney approves, in 1 to 2 weeks.";
const URL = "https://pageonelawyers.com/services/legal-content-writing";
const OG_IMAGE = ogImageFor("service", "content-writing");
const SCHEMAS = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://pageonelawyers.com/services/legal-content-writing#webpage",
    url: "https://pageonelawyers.com/services/legal-content-writing",
    name: "Legal Content Writing for Law Firms | PageOne Lawyers",
    description:
      "Legal content writing for law firms by experienced legal writers. Practice pages, blog posts and website copy your attorney approves, in 1 to 2 weeks.",
    inLanguage: "en",
    isPartOf: { "@type": "WebSite", name: "PageOne Lawyers", url: "https://pageonelawyers.com/" },
    breadcrumb: { "@id": "https://pageonelawyers.com/services/legal-content-writing#breadcrumb" },
    mainEntity: { "@id": "https://pageonelawyers.com/services/legal-content-writing#service" },
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://pageonelawyers.com/services/legal-content-writing#service",
    name: "Legal Content Writing for Law Firms",
    serviceType: "Legal content writing",
    description:
      "Legal content writing and content marketing for law firms: practice area and case-type pages, legal blog writing, website copy, attorney bios, FAQs and content refreshes, written by experienced legal writers and approved by an attorney at the client firm before publishing.",
    url: "https://pageonelawyers.com/services/legal-content-writing",
    provider: {
      "@type": "Organization",
      name: "PageOne Lawyers",
      url: "https://pageonelawyers.com/",
    },
    audience: { "@type": "BusinessAudience", audienceType: "Law firms and attorneys" },
    areaServed: { "@type": "Country", name: "United States" },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": "https://pageonelawyers.com/services/legal-content-writing#breadcrumb",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://pageonelawyers.com/" },
      {
        "@type": "ListItem",
        position: 2,
        name: "Services",
        item: "https://pageonelawyers.com/services",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Legal Content Writing",
        item: "https://pageonelawyers.com/services/legal-content-writing",
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://pageonelawyers.com/services/legal-content-writing#faq",
    mainEntity: [
      {
        "@type": "Question",
        name: "Who writes the content?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Experienced writers who specialize in legal writing for law firms. They research each topic properly and write in plain language for clients rather than other lawyers.",
        },
      },
      {
        "@type": "Question",
        name: "Who checks the legal accuracy?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "An attorney at your firm. Every piece is sent to you for review before it's published, so you can confirm accuracy, check your state's advertising rules and request changes. Nothing goes live without your approval.",
        },
      },
      {
        "@type": "Question",
        name: "Do you use AI to write content?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "We use AI tools to help with research only. Every word of the final copy is written by a person, and every piece is reviewed and approved by your attorney before it's published.",
        },
      },
      {
        "@type": "Question",
        name: "How long does it take to get content?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Most pieces are delivered within 1 to 2 weeks, depending on length and how many pieces are in the batch.",
        },
      },
      {
        "@type": "Question",
        name: "How much does legal content writing cost?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Content writing is priced separately from our SEO plans. We quote after a short scoping call, based on the number, type and length of the pieces you need.",
        },
      },
      {
        "@type": "Question",
        name: "Is the content optimized for SEO?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Every piece is planned from keyword research, and we add titles, descriptions, headings, internal links and structured data. This is SEO content writing for law firms, built to rank and to be read.",
        },
      },
      {
        "@type": "Question",
        name: "Can you update our existing lawyer website content?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. We can refresh pages that are out of date or underperforming, and rewrite law firm web content that no longer reflects your practice.",
        },
      },
      {
        "@type": "Question",
        name: "Who owns the content you write?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "You do. All content we write for your firm belongs to your firm.",
        },
      },
      {
        "@type": "Question",
        name: "What types of law firm content do you write?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Practice area and case-type pages, blog posts, website copy, attorney bios, location pages, FAQs and content refreshes.",
        },
      },
    ],
  },
];
const FAQS = [
  {
    q: "Who writes the content?",
    a: "Experienced writers who specialize in legal writing for law firms. They research each topic properly and write in plain language for clients rather than other lawyers.",
  },
  {
    q: "Who checks the legal accuracy?",
    a: "An attorney at your firm. Every piece is sent to you for review before it's published, so you can confirm accuracy, check your state's advertising rules and request changes. Nothing goes live without your approval.",
  },
  {
    q: "Do you use AI to write content?",
    a: "We use AI tools to help with research only. Every word of the final copy is written by a person, and every piece is reviewed and approved by your attorney before it's published.",
  },
  {
    q: "How long does it take to get content?",
    a: "Most pieces are delivered within 1 to 2 weeks, depending on length and how many pieces are in the batch.",
  },
  {
    q: "How much does legal content writing cost?",
    a: "Content writing is priced separately from our SEO plans. We quote after a short scoping call, based on the number, type and length of the pieces you need.",
  },
  {
    q: "Is the content optimized for SEO?",
    a: "Yes. Every piece is planned from keyword research, and we add titles, descriptions, headings, internal links and structured data. This is SEO content writing for law firms, built to rank and to be read.",
  },
  {
    q: "Can you update our existing lawyer website content?",
    a: "Yes. We can refresh pages that are out of date or underperforming, and rewrite law firm web content that no longer reflects your practice.",
  },
  {
    q: "Who owns the content you write?",
    a: "You do. All content we write for your firm belongs to your firm.",
  },
  {
    q: "What types of law firm content do you write?",
    a: "Practice area and case-type pages, blog posts, website copy, attorney bios, location pages, FAQs and content refreshes.",
  },
];

export const Route = createFileRoute("/services/legal-content-writing")({
  head: () => applyPageMetadata("/services/legal-content-writing", {
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:type", content: "image/svg+xml" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Legal Content Writing — PageOne Lawyers" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: SCHEMAS.map(jsonLdScript),
  }),
  component: LegalContentWritingPage,
});

function LegalContentWritingPage() {
  return (
    <Layout>
      <section className="bg-hero-gradient text-primary-foreground">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-6 text-xs text-primary-foreground/70">
            <ol className="flex flex-wrap gap-1">
              <li>
                <Link to="/">Home</Link> /
              </li>
              <li>
                <Link to="/services">Services</Link> /
              </li>
              <li>Legal Content Writing</li>
            </ol>
          </nav>
          <Eyebrow>
            <span className="text-gold">Service</span>
          </Eyebrow>
          <h1 className="mt-6 max-w-4xl font-display text-4xl font-semibold leading-tight sm:text-5xl md:text-6xl">
            {"Legal Content Writing and Content Marketing for Law Firms"}
          </h1><LastUpdated />
          <p className="mt-6 max-w-3xl text-xl text-primary-foreground/85">
            {
              "Practice pages, blog posts and website copy written by experienced legal writers, approved by your attorneys, and planned to bring in the cases you want."
            }
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <CTAButton to="#free-plan" variant="gold">
              Get a free content review
            </CTAButton>
            <CTAButton to={BOOKING_URL} variant="ghostLight">
              Talk to a strategist
            </CTAButton>
          </div>
        </div>
      </section>
      <Section>
        <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
          {
            "Legal content writing has two jobs: answer the questions a worried client is asking, and give Google a clear reason to show your firm when they ask them. We write law firm website content and blog posts that do both. Every piece is planned from search and case-value data, written by people who know legal writing, and sent to an attorney at your firm for review and approval before anything is published."
          }
        </p>
      </Section>
      <div className="border-y border-border bg-secondary/40">
        <Section>
          <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">
            {"What's Included in Our Legal Content Writing Services"}
          </h2>
          <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
            {
              "Our content writing for law firms covers every page a client reads before they decide to call, not just the blog."
            }
          </p>
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"Practice area pages"}</h3>
              <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
                {
                  "The pages that bring in most of your cases. Each one explains what you handle, what a client should do next, how fees work and what happens in a consultation, in plain language."
                }
              </p>
            </article>
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"Case-type pages"}</h3>
              <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
                {
                  "Separate pages for the specific matters you want more of, such as truck accidents within personal injury or child custody within family law. They capture searches a general practice page can't."
                }
              </p>
            </article>
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"Legal blog writing"}</h3>
              <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
                {
                  "Posts that answer the real questions clients type into Google, each linked to the practice page it supports. Our legal blog writing service covers topic research, writing, internal links and publishing."
                }
              </p>
            </article>
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"Website copy and attorney bios"}</h3>
              <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
                {
                  "Our law firm website content writing covers the homepage, about page, location pages and attorney bios. Good law firm copywriting is specific: who you help, what you handle and what happens when someone calls. This is the law firm website content that turns a visitor into someone who trusts you enough to call."
                }
              </p>
            </article>
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"FAQs and quick answers"}</h3>
              <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
                {
                  "Short, direct answers to common questions, written so both readers and AI search features can pick them up easily."
                }
              </p>
            </article>
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"Content refreshes"}</h3>
              <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
                {
                  "Updating older pages when the law changes or the facts move on, so your content stays accurate and keeps ranking. We only change the date when the content has really changed."
                }
              </p>
            </article>
          </div>
        </Section>
      </div>
      <Section>
        <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">
          {"How Every Piece of Law Firm Content Is Made"}
        </h2>
        <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
          {
            "Good legal web content comes from a clear process, not a word count. Legal content creation at PageOne follows the same six steps for every page and post."
          }
        </p>
        <figure className="mt-8 overflow-hidden rounded-lg border border-border bg-background">
          <img
            src="/images/services/legal-content-writing/legal-content-workflow.svg"
            alt="Six steps from brief to refresh: brief and keyword plan, research, writing by an experienced legal writer, review and approval by an attorney at your firm, SEO and publishing, then tracking and refreshing."
            width="1200"
            height="520"
            loading="lazy"
            decoding="async"
            className="h-auto w-full"
          />
          <figcaption className="border-t border-border px-5 py-3 text-sm text-muted-foreground">
            {"Nothing is published until an attorney at your firm has reviewed and approved it."}
          </figcaption>
        </figure>
        <div className="mt-8 grid gap-4 md:grid-cols-2">
          <article className="rounded-lg border border-border bg-background p-6">
            <h3 className="text-xl font-semibold">{"Brief and keyword plan"}</h3>
            <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
              {
                "We choose topics from search data and the value of the cases behind them, so effort goes where the cases are."
              }
            </p>
          </article>
          <article className="rounded-lg border border-border bg-background p-6">
            <h3 className="text-xl font-semibold">{"Research"}</h3>
            <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
              {
                "Our writers research statutes, court rules and your firm's own approach. AI tools help with research only; every word of the final copy is written by a person."
              }
            </p>
          </article>
          <article className="rounded-lg border border-border bg-background p-6">
            <h3 className="text-xl font-semibold">{"Writing"}</h3>
            <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
              {
                "Experienced legal writers draft each piece in plain language for someone who has never spoken to a lawyer before."
              }
            </p>
          </article>
          <article className="rounded-lg border border-border bg-background p-6">
            <h3 className="text-xl font-semibold">{"Your attorney reviews and approves"}</h3>
            <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
              {
                "Every piece goes to an attorney at your firm before it's published. You check the legal accuracy and your state's advertising rules, request any changes, and approve the final version. Your attorney can also be named as the reviewer on the page."
              }
            </p>
          </article>
          <article className="rounded-lg border border-border bg-background p-6">
            <h3 className="text-xl font-semibold">{"SEO and publishing"}</h3>
            <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
              {
                "We add titles, descriptions, headings, internal links and structured data, then publish to your site or hand it to your team."
              }
            </p>
          </article>
          <article className="rounded-lg border border-border bg-background p-6">
            <h3 className="text-xl font-semibold">{"Tracking and refreshing"}</h3>
            <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
              {
                "We track which pages bring calls and form submissions, and refresh pages when the law or the facts change."
              }
            </p>
          </article>
        </div>
      </Section>
      <div className="border-y border-border bg-secondary/40">
        <Section>
          <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">
            {"Law Firm Content Marketing That Works as a System"}
          </h2>
          <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
            {
              "Content marketing for law firms, like any legal content marketing, works best when each piece has a clear job and links to the others. A blog post answers an early question and points to the case-type page. The case-type page builds confidence and points to the practice page. The practice page makes it easy to call. Content marketing for lawyers fails when it's a stream of unconnected posts with no path to a consultation."
            }
          </p>
          <figure className="mt-8 overflow-hidden rounded-lg border border-border bg-background">
            <img
              src="/images/services/legal-content-writing/content-types-roles.svg"
              alt="Five content types and their jobs: practice pages convert, case-type pages capture specific searches, blog posts answer questions, FAQs give quick answers, and attorney bios build trust."
              width="1200"
              height="560"
              loading="lazy"
              decoding="async"
              className="h-auto w-full"
            />
            <figcaption className="border-t border-border px-5 py-3 text-sm text-muted-foreground">
              {
                "Each type of content has a different job, and they link together toward a consultation."
              }
            </figcaption>
          </figure>
        </Section>
      </div>
      <Section>
        <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">{"Plan Your First Pages"}</h2>
        <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
          {
            "Pick up to three practice areas and what you want most. You'll get the pages to write first, with example topics."
          }
        </p>
        <ContentPlanBuilder />
      </Section>
      <section id="free-plan" className="scroll-mt-24 bg-hero-gradient text-primary-foreground">
        <div className="mx-auto max-w-7xl px-5 py-14 sm:px-6 sm:py-20">
          <HeroLeadForm
            layout="split"
            headingLevel="h2"
            eyebrow={"FREE CONTENT REVIEW"}
            heading={"Get a free review of your law firm's content."}
            intro={
              "Send us your website and we'll come back within 24 hours with what's working, what's missing and which pages to write first."
            }
            sideContent={
              <>
                <p className="mt-7 text-sm font-bold">What we'll look at for your firm:</p>
                <ul className="mt-4 space-y-4 text-sm leading-relaxed text-primary-foreground/80">
                  <li className="flex items-start gap-3">
                    <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                    <span className="min-w-0">
                      {"Whether each practice area and case type you want has its own page"}
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                    <span className="min-w-0">
                      {"How your current pages read to someone who has never hired a lawyer"}
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                    <span className="min-w-0">
                      {"Which client questions your competitors answer and you don't"}
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                    <span className="min-w-0">
                      {"Which pages could be refreshed rather than rewritten"}
                    </span>
                  </li>
                </ul>
                <p className="mt-6 text-xs text-primary-foreground/60">
                  Prefer to talk first?{" "}
                  <a
                    href={BOOKING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gold underline underline-offset-4"
                  >
                    Book a strategy call
                  </a>
                </p>
              </>
            }
          />
        </div>
      </section>
      <Section>
        <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">
          {"Content for Lawyers in Every Practice Area"}
        </h2>
        <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
          {
            "The questions clients ask, and the tone that earns their trust, change from one practice to the next. Content marketing for attorneys works the same way in every practice area, but the topics and tone don't. We match writers and topics to the practice areas you want to grow."
          }
        </p>
        <ul className="mt-8 grid gap-3 sm:grid-cols-2">
          <li>
            <Link
              to="/practice-areas/$slug"
              params={{ slug: "personal-injury-lawyer-seo" }}
              className="block rounded-lg border border-border p-4 font-semibold text-primary transition hover:border-gold hover:text-gold-deep"
            >
              {"content marketing for personal injury law firms"}
            </Link>
          </li>
          <li>
            <Link
              to="/practice-areas/$slug"
              params={{ slug: "family-law-lawyer-seo" }}
              className="block rounded-lg border border-border p-4 font-semibold text-primary transition hover:border-gold hover:text-gold-deep"
            >
              {"divorce attorney content marketing"}
            </Link>
          </li>
          <li>
            <Link
              to="/practice-areas/$slug"
              params={{ slug: "criminal-defense-lawyer-seo" }}
              className="block rounded-lg border border-border p-4 font-semibold text-primary transition hover:border-gold hover:text-gold-deep"
            >
              {"criminal defense law firm content writing"}
            </Link>
          </li>
          <li>
            <Link
              to="/practice-areas/$slug"
              params={{ slug: "dui-dwi-lawyer-seo" }}
              className="block rounded-lg border border-border p-4 font-semibold text-primary transition hover:border-gold hover:text-gold-deep"
            >
              {"DUI lawyer content writing"}
            </Link>
          </li>
          <li>
            <Link
              to="/practice-areas/$slug"
              params={{ slug: "estate-planning-lawyer-seo" }}
              className="block rounded-lg border border-border p-4 font-semibold text-primary transition hover:border-gold hover:text-gold-deep"
            >
              {"content marketing for estate planning lawyers"}
            </Link>
          </li>
          <li>
            <Link
              to="/practice-areas/$slug"
              params={{ slug: "immigration-lawyer-seo" }}
              className="block rounded-lg border border-border p-4 font-semibold text-primary transition hover:border-gold hover:text-gold-deep"
            >
              {"immigration attorney content marketing"}
            </Link>
          </li>
          <li>
            <Link
              to="/practice-areas/$slug"
              params={{ slug: "corporate-business-lawyer-seo" }}
              className="block rounded-lg border border-border p-4 font-semibold text-primary transition hover:border-gold hover:text-gold-deep"
            >
              {"business law content writing"}
            </Link>
          </li>
        </ul>
        <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
          {
            "Whether you need a family law content writer who understands custody disputes, criminal law blog writing services that explain charges without jargon, or business attorney content writing and corporate law blog writing for company owners, the brief starts with the clients you want."
          }
        </p>
      </Section>
      <div className="border-y border-border bg-secondary/40">
        <Section>
          <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">
            {"Legal Content Writing Pricing"}
          </h2>
          <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
            {
              "Content writing is a separate service from our SEO plans, so you only pay for the content you need. We quote after a short scoping call, based on the number and type of pieces, their length, and whether we're writing new content or refreshing what you have."
            }
          </p>
          <ul className="mt-8 space-y-3">
            <li className="flex items-start gap-3">
              <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
              <span className="min-w-0">{"A clear quote agreed before writing starts"}</span>
            </li>
            <li className="flex items-start gap-3">
              <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
              <span className="min-w-0">{"Most pieces delivered within 1 to 2 weeks"}</span>
            </li>
            <li className="flex items-start gap-3">
              <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
              <span className="min-w-0">
                {"An attorney at your firm approves every piece before it's published"}
              </span>
            </li>
            <li className="flex items-start gap-3">
              <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
              <span className="min-w-0">{"You own all the content we write"}</span>
            </li>
          </ul>
          <div className="mt-8">
            <CTAButton to="#free-plan" variant="gold">
              Get a free content review
            </CTAButton>
          </div>
        </Section>
      </div>
      <Section>
        <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">
          {"Why Firms Choose PageOne as Their Legal Content Writers"}
        </h2>
        <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
          {
            "We only write for law firms, so our writers already understand how people look for a lawyer and what they need to read before they call. As a content marketing agency for law firms that also does SEO, we plan every piece around the cases you want and track what it brings in. Your attorneys keep the final say on every word, and you own everything we write. Lawyer content marketing only pays off when it's accurate, consistent and connected to how clients actually reach you, and that's how we run it."
          }
        </p>
        <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
          {
            "If you're comparing outsourced legal content writing options, or trying to find the best legal content writers for your firm, ask each one: who writes the content, who checks the law, how topics are chosen, and how you'll see which pages bring in clients. A good law firm content writer should answer all four without hesitation."
          }
        </p>
      </Section>
      <CaseStudy {...serviceCaseStudy("Legal Content Writing")} semanticSubheadings />
      <TrustSignals
        semanticSubheadings
        hiddenCredentials={["Attorney-reviewed content", "ABA Model Rule 7"]}
        intro="Every legal content writing engagement is delivered by the same senior team, on the same guarantees, with reporting tied to signed cases — not clicks."
      />
      <FAQSection
        tone="muted"
        title={"Legal Content Writing: Questions Firms Ask"}
        items={FAQS}
        semanticQuestionHeadings
      />
      <section className="bg-hero-gradient text-primary-foreground">
        <div className="mx-auto max-w-5xl px-5 py-14 text-center sm:px-6 sm:py-20">
          <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">
            {"Ready for Content That Brings In Clients?"}
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-primary-foreground/80">
            {
              "Get a free review of your current content, with a clear list of the pages to write or refresh first."
            }
          </p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row sm:flex-wrap">
            <CTAButton to="#free-plan" variant="gold">
              Get my free content review
            </CTAButton>
            <CTAButton to={BOOKING_URL} variant="ghostLight">
              Book a strategy call
            </CTAButton>
          </div>
        </div>
      </section>
      <Section>
        <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">{"Related Services"}</h2>
        <ul className="mt-6 flex flex-wrap gap-2">
          {services
            .filter((item) => item.slug !== "legal-content-writing")
            .map((item) => (
              <li key={item.slug}>
                <Link
                  to="/services/$slug"
                  params={{ slug: item.slug }}
                  className="inline-block rounded-full border border-border px-4 py-2 text-sm transition hover:border-gold hover:text-gold-deep"
                >
                  {item.name}
                </Link>
              </li>
            ))}
        </ul>
      </Section>
    </Layout>
  );
}
